// Credits order price calculation ****************************************************************
// pathname: /v1/order/account-discount-data-with-amount
// ************************************************************************************************
import { useSwrApi } from 'src/hooks/useSwrApi';
import { ApiOrderCreditsPost } from '@type/v1-api-types';

/**
 * @description Hook interface.
 */
interface UseSwrAccountDiscountData {
  locale: string,
  postData?: ApiOrderCreditsPost,
  fetchCondition?: boolean,
}


/**
 * @description SWR: fetch order price calculation. The hook will automatically fetch the data when
 * the postData changes (mutation). It will not fetch anything if the postData is undefined or input value is less than
 * zero. We leverage the dedupingInterval to avoid fetching the same data (postData key) multiple times and use the data
 * already cached.
 * - `locale` string - the locale to use for the request. Default 'ro'.
 * - `postData` ApiOrderCreditsPost - the data we want to send to the server. Default undefined.
 * - `fetchCondition` boolean - the condition to fetch the data. Default undefined.
 */
export const useSwrAccountDiscountData = (props: UseSwrAccountDiscountData) => {
  // Destructure props
  const {
    locale,
    postData,
    fetchCondition,
  } = props;


  const url = postData ? `/v1/order/account-discount-data-with-amount/${postData?.creditAmount}` : '/v1/order/account-discount-data-with-amount';

  // SWR wrapper
  const {
    data, error, isValidating, isLoading, mutate,
  } = useSwrApi<ApiSchema<'AccountDiscountData'>>({
    url,
    locale,
    // We will fetch the data conditionally to avoid unneeded requests.
    shouldFetch: fetchCondition,
    fetcherConfig: {
      method: 'GET',
      stringifyPayload: true,
    },
    apiOptions: {
      revalidateOnFocus: false,
      dedupingInterval: 1000 * 60 * 5, // 5 minutes
    },
    toastsOptions: {
      showErrorToast: true,
    },
  });

  // return data
  return {
    data,
    error,
    isValidating,
    isLoading,
    mutate,
  };
};

